import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, Input, AfterViewInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KEYS, MessageToUiService } from 'src/app/services/message-to-ui.service';
import { SettingsService } from 'src/app/services/settings.service';
import { IUploadDocumentRestrictions } from '../models';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { map } from 'rxjs';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css'],
  imports: [CommonModule, TranslateModule, MatTooltipModule]
})
export class FileUploadComponent implements OnInit, AfterViewInit {

  @ViewChild('fileLabel') fileLabel: ElementRef;
  files: File[];
  @Output() fileToUploadChange: EventEmitter<File[]> = new EventEmitter<File[]>();
  @Output() uploadedFilesValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() multiFile = false;
  @Input() icon = false;
  @Input() customTemplate = false;
  @Input() displayFilesList = true;
  @Input() fileInputId = 'fileInput';
  @Input() multipleUploads = false;
  @Input() fileType: 'file' | 'invoice' = 'file';

  private _fileUpload: HTMLInputElement;
  private _fileSelectionError: boolean = false;
  uploadRestrictions: IUploadDocumentRestrictions;

  constructor(
    private settingsService: SettingsService,
    private messageToUiService: MessageToUiService,
    private translateService: TranslateService) { }

  ngOnInit() { 
    if (this.fileType === 'invoice') {
      this.settingsService.getInvoiceFileUploadRestrictions()
      .pipe(
        map(result => {
          result.allowedFileExtensions = result.allowedFileExtensions.map(afe => afe.startsWith('.') ? afe : ('.' + afe));
          return result;
      }))
      .subscribe(_ => {
        this.uploadRestrictions = _
      }
      );
    }
    else {
      this.settingsService.getFileUploadRestrictions()
       .pipe(
          map(result => {
            result.allowedFileExtensions = result.allowedFileExtensions.map(afe => afe.startsWith('.') ? afe : ('.' + afe));
            return result;
      }))
      .subscribe(_ => {
        this.uploadRestrictions = _;
      });
    }
    if (this.icon) { this.displayFilesList = false; }
  }

  ngAfterViewInit() {
    this._fileUpload = document.getElementById(this.fileInputId) as HTMLInputElement;
    this._fileUpload.onchange = () => {
      if (this.multipleUploads) {
        const selectedFiles = Array.from(this._fileUpload.files);

        selectedFiles.forEach(newFile => {
          if (this.files) {
            this.files = this.files.filter(file => file.name !== newFile.name);
          }
        });

        this.files = this.files ? this.files.concat(selectedFiles) : selectedFiles;
      }
      else {
        this.files = Array.from(this._fileUpload.files);
      }

      this.validateFiles();
      if (!this.files?.length) { this.clear(); return; }

      this.fileToUploadChange.emit(this.files);
      this.uploadedFilesValid.emit(this._fileSelectionError);

      if (!this.icon && this.fileLabel) {
        this.fileLabel.nativeElement.innerText = this.files?.length > 1 ? `${this.files.length} files` : this.files?.[0]?.name;
      }
      this._fileUpload.value = null;
    };
  }

  selectFile() {
    const fileUpload = document.getElementById(this.fileInputId) as HTMLInputElement;
    fileUpload?.click();
  }

  clear() {
    this.files = null;
    if (this.displayFilesList) { this.fileLabel.nativeElement.innerText = ""; }
    this.fileToUploadChange.emit(this.files);
    this._fileSelectionError = false;
    this.uploadedFilesValid.emit(this._fileSelectionError);
  }

  validateFiles() {
    const displayFileName = this.files.length > 1;
    this._fileSelectionError = false;

    let filesToValidate = [...this.files];

    for (let i = filesToValidate.length - 1; i >=0 ; i--) {
      const fileSizeMb = filesToValidate[i].size / (1024 * 1024);
      let errorMessage = '';

      if (fileSizeMb > this.uploadRestrictions.maxSizeMb) {
        errorMessage = this.translateService.instant('Error.AttachmentSizeExceedsMax', { maxSizeMb: this.uploadRestrictions.maxSizeMb });
      }

      const fileExtension = this.getFileExtension(filesToValidate[i].name);

      if (this.uploadRestrictions.allowedFileExtensions.indexOf(fileExtension) < 0) {
        errorMessage = this.translateService.instant('Error.AttachmentExtensionNotAllowed', { allowedFileTypes: this.uploadRestrictions.allowedFileExtensions.join(', ') });
      }

      if (errorMessage.length) {
        this.messageToUiService.error(displayFileName ? filesToValidate[i].name : KEYS.Error_GenericMessage, errorMessage, true);
        this.files.splice(i, 1);
        this._fileSelectionError = true;
      }
    };
  }

  private getFileExtension(fileName: string): string {
    const fileNameSplit = fileName.split('.');
    return '.' + fileNameSplit[fileNameSplit.length - 1];
  }
}
